import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { usePmProperty } from '@/hooks/usePmProperty';
import { getPropertyModules, setModuleActive } from '@/services/api.client';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { PropertyModule } from '@/types';

export function ModulesPage() {
  const pm = usePmProperty();
  const propertyId = pm?.propertyId ?? '';
  const qc = useQueryClient();
  const [pending, setPending] = useState<{ module: PropertyModule; nextActive: boolean } | null>(
    null,
  );

  const query = useQuery({
    queryKey: ['pm', 'modules', propertyId],
    queryFn: () => getPropertyModules(propertyId),
    enabled: !!propertyId,
  });

  const mutation = useMutation({
    mutationFn: ({ moduleId, active }: { moduleId: string; active: boolean }) =>
      setModuleActive(propertyId, moduleId, active),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'modules', propertyId] });
      setPending(null);
    },
  });

  const modules = query.data ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl">Feature Modules</h1>
      <p className="text-sm text-[var(--text-tertiary)]">
        Enable or disable platform modules for this property. Core modules cannot be turned off.
      </p>

      {query.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {modules.map((mod) => (
            <div
              key={mod.id}
              className="rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-surface-raised)] p-4 flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium">{mod.name}</p>
                  <p className="text-sm text-[var(--text-tertiary)] mt-0.5">{mod.description}</p>
                </div>
                {mod.alwaysOn ? (
                  <Badge variant="secondary" className="shrink-0 flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    Always Active
                  </Badge>
                ) : (
                  <Switch
                    checked={mod.active}
                    disabled={mutation.isPending}
                    onCheckedChange={(checked) => setPending({ module: mod, nextActive: checked })}
                    aria-label={`Toggle ${mod.name}`}
                  />
                )}
              </div>
              {(mod.dependsOn?.length ?? 0) > 0 && (
                <p className="text-xs text-[var(--status-amber-text)]">
                  Requires: {mod.dependsOn?.join(', ')}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={!!pending}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pending?.nextActive ? 'Enable' : 'Disable'} {pending?.module.name}?
            </DialogTitle>
            <DialogDescription>
              {pending?.nextActive
                ? `This will activate ${pending.module.name} features for all users on this property.`
                : `Disabling ${pending?.module.name} will hide its features from users. Existing data is not deleted.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button
              variant={pending?.nextActive ? 'default' : 'destructive'}
              disabled={mutation.isPending}
              onClick={() => {
                if (!pending) return;
                mutation.mutate({ moduleId: pending.module.id, active: pending.nextActive });
              }}
            >
              {pending?.nextActive ? 'Enable' : 'Disable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
